import React from 'react';
import type { LucideIcon } from 'lucide-react';
import { cn } from '@/lib/utils';

/**
 * One row of the bell panel: icon tile, title, a quiet detail line. Urgent
 * rows sit on the brand tint with a brand icon — the only colour in the panel,
 * except for `danger` (a blocked number), which is red.
 */
export interface NotificationRowProps {
  icon: LucideIcon;
  title: React.ReactNode;
  detail?: React.ReactNode;
  meta?: React.ReactNode;
  urgent?: boolean;
  /** Something is broken, not just waiting (blocked WhatsApp number): red instead of the brand accent. */
  danger?: boolean;
  unread?: boolean;
  muted?: boolean;
  onClick: () => void;
  /**
   * The row leads to a page outside the app: a real link that opens in a new
   * tab (not a script — no pop-up blocker can get between the click and the
   * tab). `onClick` still runs, e.g. to mark the entry read; it must not cancel the click.
   */
  href?: string;
  /** Extra action outside the row button (e.g. „Anrufen“), never nested inside it. */
  aside?: React.ReactNode;
}

export function NotificationRow({
  icon: Icon,
  title,
  detail,
  meta,
  urgent = false,
  danger = false,
  unread = false,
  muted = false,
  onClick,
  href,
  aside,
}: NotificationRowProps) {
  const rowClassName = cn(
    'flex min-w-0 flex-1 items-start gap-3 rounded-xl px-2.5 py-2 text-left transition-colors',
    'hover:bg-foreground/[0.04] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand/60',
    urgent && (danger ? 'hover:bg-destructive/[0.14]' : 'hover:bg-brand/[0.14]'),
  );
  const content = (
    <>
      <span
        aria-hidden="true"
        className={cn(
          'mt-0.5 grid h-7 w-7 shrink-0 place-items-center rounded-lg border bg-background/70',
          urgent
            ? danger
              ? 'border-destructive/50 text-destructive-strong'
              : 'border-brand/50 text-brand'
            : 'border-border text-muted-foreground',
        )}
      >
        <Icon className="h-3.5 w-3.5" />
      </span>
      <span className="min-w-0 flex-1">
        <span
          className={cn(
            'line-clamp-2 break-words text-[13px] leading-5',
            unread || urgent ? 'font-semibold text-foreground' : 'font-medium text-foreground/90',
            muted && 'text-muted-foreground',
          )}
        >
          {title}
        </span>
        {detail && (
          <span className="mt-0.5 line-clamp-2 break-words text-xs leading-4 text-muted-foreground tabular-nums">
            {detail}
          </span>
        )}
      </span>
      {(meta || unread) && (
        <span className="flex shrink-0 flex-col items-end gap-1 pt-0.5">
          {meta && <span className="text-[11px] leading-4 text-muted-foreground tabular-nums">{meta}</span>}
          {unread && (
            <span
              aria-hidden="true"
              className={cn(
                'h-2 w-2 rounded-full',
                urgent ? (danger ? 'bg-destructive' : 'bg-brand') : 'bg-foreground/35',
              )}
            />
          )}
        </span>
      )}
    </>
  );

  return (
    <li
      className={cn(
        'group relative flex items-stretch rounded-xl',
        urgent && (danger ? 'bg-destructive/10' : 'bg-brand-soft'),
      )}
    >
      {href ? (
        <a href={href} target="_blank" rel="noopener noreferrer" onClick={onClick} className={rowClassName}>
          {content}
        </a>
      ) : (
        <button type="button" onClick={onClick} className={rowClassName}>
          {content}
        </button>
      )}
      {aside && <span className="flex shrink-0 items-center pr-2">{aside}</span>}
    </li>
  );
}

export function PanelSectionTitle({ id, children, action }: { id: string; children: React.ReactNode; action?: React.ReactNode }) {
  return (
    <div className="flex items-center justify-between px-2.5 pb-1 pt-3">
      <h2 id={id} className="text-[10.5px] font-semibold uppercase tracking-[0.08em] text-muted-foreground">
        {children}
      </h2>
      {action}
    </div>
  );
}

export default NotificationRow;
